import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Loader2, RefreshCw, Download } from 'lucide-react';
import { getApiBaseUrl } from '../config/backend.config';
import { useClerk } from '@clerk/clerk-react';
import './SyncWorkspaceModal.css';

function SyncWorkspaceModal({ isOpen, onClose, selectedWorkspace, onSync }) {
  const clerk = useClerk();
  const [isSyncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [forceSync, setForceSync] = useState(false);
  const [versionStatus, setVersionStatus] = useState(null);
  const [loadingVersionStatus, setLoadingVersionStatus] = useState(false);

  const fetchVersionStatus = async () => {
    if (!selectedWorkspace?.workspace_id) return;

    setLoadingVersionStatus(true);
    try {
      const token = await clerk.session?.getToken();
      if (!token) return;

      const response = await fetch(
        `${getApiBaseUrl()}/workspaces/${selectedWorkspace.workspace_id}/version-status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        console.error('Error fetching version status:', response.statusText);
        return;
      }

      const status = await response.json();
      setVersionStatus(status);
    } catch (error) {
      console.error('Error fetching version status:', error);
    } finally {
      setLoadingVersionStatus(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSyncing(false);
      setSyncResult(null);
      setForceSync(false);
      fetchVersionStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedWorkspace?.workspace_id, clerk]);

  const handleSync = async () => {
    if (!selectedWorkspace) return;

    setSyncing(true);
    setSyncResult(null);

    try {
      const result = await onSync(selectedWorkspace, forceSync);
      setSyncResult(result);

      if (result.success) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncResult({
        success: false,
        error: error.message || 'Sync failed. Please try again.'
      });
    } finally {
      setSyncing(false);
    }
  };

  if (!isOpen) return null;

  const hasUpdate = versionStatus?.has_newer_version;

  return (
    <div className="sync-modal-overlay">
      <div className="sync-modal">
        <div className="sync-modal-header">
          <div className="sync-modal-title">
            <Download size={20} />
            <span>Sync from Remote</span>
          </div>
          <button
            className="sync-modal-close"
            onClick={onClose}
            disabled={isSyncing}
          >
            <X size={18} />
          </button>
        </div>

        <div className="sync-modal-content">
          {!syncResult ? (
            <>
              {/* Status indicator */}
              <div className="sync-section">
                {loadingVersionStatus ? (
                  <div className="sync-status-loading">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Checking remote...</span>
                  </div>
                ) : versionStatus ? (
                  <div className={`sync-status-badge ${hasUpdate ? 'has-update' : 'up-to-date'}`}>
                    {hasUpdate ? (
                      <>
                        <AlertTriangle size={18} />
                        <span>A newer version is available on the remote</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        <span>Your workspace is up to date</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="sync-status-badge unknown">
                    <RefreshCw size={18} />
                    <span>Unable to check remote status</span>
                  </div>
                )}
              </div>

              {/* Force sync option */}
              <div className="sync-section">
                <label className="force-sync-option">
                  <input
                    type="checkbox"
                    checked={forceSync}
                    onChange={(e) => setForceSync(e.target.checked)}
                    disabled={isSyncing}
                  />
                  <span className="force-sync-label">
                    <strong>Force sync</strong>
                    <p>Overwrite local files with the remote version</p>
                  </span>
                </label>
              </div>

              {/* Indeterminate loading bar */}
              {isSyncing && (
                <div className="sync-progress">
                  <div className="progress-bar">
                    <div className="progress-fill-indeterminate" />
                  </div>
                  <span className="sync-progress-label">Pulling from remote...</span>
                </div>
              )}
            </>
          ) : (
            /* Result */
            <div className="sync-result">
              {syncResult.success ? (
                <div className="result-success">
                  <CheckCircle size={40} className="result-icon success" />
                  <h3>Synced</h3>
                  <p>{syncResult.message || 'Workspace is up to date.'}</p>
                </div>
              ) : (
                <div className="result-error">
                  <AlertTriangle size={40} className="result-icon error" />
                  <h3>Sync Failed</h3>
                  <p>{syncResult.error}</p>
                  <button
                    className="retry-button"
                    onClick={() => setSyncResult(null)}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sync-modal-footer">
          {!syncResult ? (
            <>
              <button
                className="sync-cancel-button"
                onClick={onClose}
                disabled={isSyncing}
              >
                Cancel
              </button>
              <button
                className="sync-confirm-button"
                onClick={handleSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Sync
                  </>
                )}
              </button>
            </>
          ) : syncResult.success ? (
            <button className="sync-close-button" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SyncWorkspaceModal;
